<template>
  <div>
    <div class="operations-subtitle-container">
      <p class="operations-subtitle">{{ $t('operations.subtitle') }}</p>
      <el-button
        type="info"
        size="small"
        @click="handleAnalysisWithoutOperationDialogOpen"
      >
        {{ $t('operations.analysesWithoutOperations.button.label') }}
      </el-button>
    </div>

    <OperationsSearch @handle-operations-list-update="getOperations" />

    <AnalysisDetailCard
      v-if="forms.operations.analysisId && analysis"
      :analysis="analysis"
    />

    <el-form
      v-if="forms.operations.analysisId"
      size="small"
      label-position="top"
    >
      <el-row
        :gutter="20"
        type="flex"
      >
        <el-col :span="4">
          <el-form-item :label="$t('operations.reportConfig.showTemperature')">
            <el-switch
              v-model="reportFlags.showTemperature"
              @change="(val) => updateReportFlag('showTemperature', val)"
            />
          </el-form-item>
        </el-col>
        <el-col :span="4">
          <el-form-item :label="$t('operations.reportConfig.showSymbol')">
            <el-switch
              v-model="reportFlags.showSymbol"
              @change="(val) => updateReportFlag('showSymbol', val)"
            />
          </el-form-item>
        </el-col>
        <el-col :span="4">
          <el-form-item :label="$t('operations.reportConfig.showDilution')">
            <el-switch
              v-model="reportFlags.showDilution"
              @change="(val) => updateReportFlag('showDilution', val)"
            />
          </el-form-item>
        </el-col>
        <el-col :span="4">
          <el-form-item :label="$t('operations.reportConfig.defaultDilution')">
            <el-select
              v-model="reportFlags.defaultDilution"
              clearable
              @change="updateDefaultDilution"
            >
              <el-option
                v-for="item in dilutions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>

    <div v-if="this.forms.operations.analysisId">
      <TableWrapper
        :table-columns="operationsColumns"
        table-key="operations"
        :title="$t('operations.table.tableTitle')"
        :enable-selection="canUseBulkApply"
        custom-bulk-action
        :custom-bulk-action-options="bulkAction"
        @editOperation="editOperation"
        @deleteOperation="deleteOperation"
        @updatePrintOnWorksheet="
          (row) => {
            patchOperation(row, { printOnWorksheet: !row.printOnWorksheet });
          }
        "
        @updatePrintOnLabels="
          (row) => {
            patchOperation(row, { printOnLabels: !row.printOnLabels });
          }
        "
        @updateMainResult="
          (row) => {
            patchOperation(row, { mainResult: !row.mainResult });
          }
        "
        @addCustomSpecification="addCustomSpecification"
      />

      <ViewActionButtons :buttons="actionButtons" />

      <CreateEditOperation
        v-if="openCreateEditOperation"
        :is-modal-edit="isModalEdit"
        :initial-data="initialOperationData"
        @dialogClose="handleDialogClose"
      />

      <CopyOperations
        :visible="openCopyToOperations"
        :selected-operations="copyToSelection"
        @dialogClose="handleCopyToClose"
      />
    </div>
    <div v-if="this.forms.operations.analysisId">
      <Filters
        :filters="specificationsFilters"
        filter-key="specifications"
        :initial-data="filtersStatus.specifications"
      />

      <SpecificationsTable
        :table-columns="specificationsColumns"
        :show-pagination="false"
        row-key="id"
        table-key="specifications"
        :title="$t('operations.table.specificationsTableTitle')"
        @editSpecification="editSpecification"
        @deleteSpecification="deleteSpecification"
      />
    </div>

    <CreateEditSpecification
      v-if="openCreateEditSpecification"
      :is-modal-edit="isSpecificationModalEdit"
      :initial-data="initialSpecificationData"
      :has-standard-specification="
        initialSpecificationData.hasStandardSpecification
      "
      @dialogClose="handleSpecificationDialogClose"
    />

    <OperationsWithoutAnalysesDialog
      v-if="openAnalysisWithoutOperations"
      @dialogClose="handleAnalysisWithoutOperationDialogClose"
    />
  </div>
</template>

<script>
  import acl from '@/services/acl';

  import { mapGetters, mapActions } from 'vuex';

  import {
    operationsColumns,
    specificationsColumns,
    specificationsFilters,
  } from '@/config';

  import { DILUTIONS } from '@/config/constants';
  import { analysesApi } from '@/api';

  import TableWrapper from '@/components/TableWrapper.vue';
  import ViewActionButtons from '@/components/ViewActionButtons.vue';
  import Filters from '@/components/Filters.vue';
  import AnalysisDetailCard from '@/components/AnalysisDetailCard.vue';

  import OperationsSearch from './OperationsSearch.vue';
  import CreateEditOperation from './CreateEditOperation.vue';
  import CopyOperations from './CopyOperations.vue';
  import SpecificationsTable from './SpecificationsTable.vue';
  import CreateEditSpecification from './CreateEditSpecification.vue';
  import OperationsWithoutAnalysesDialog from './OperationsWithoutAnalysesDialog.vue';

  export default {
    name: 'OperationsIndex',
    components: {
      TableWrapper,
      OperationsSearch,
      ViewActionButtons,
      CreateEditOperation,
      CopyOperations,
      SpecificationsTable,
      Filters,
      CreateEditSpecification,
      OperationsWithoutAnalysesDialog,
      AnalysisDetailCard,
    },
    data() {
      return {
        operationsColumns,
        specificationsColumns,
        specificationsFilters,
        openCreateEditOperation: false,
        openAnalysisWithoutOperations: false,
        isModalEdit: false,
        initialOperationData: {},
        copyToSelection: [],
        openCopyToOperations: false,
        bulkAction: {
          label: this.$t('operations.copyTo.copyOperation'),
          action: (selection) => {
            this.copySelectionTo(selection);
          },
        },
        actionButtons: [
          {
            text: this.$t('operations.create.createOperation'),
            type: 'primary',
            permissions: ['operationsManage'],
            action: () => {
              this.initialOperationData = {};
              this.openCreateEditOperation = true;
            },
          },
        ],
        openCreateEditSpecification: false,
        initialSpecificationData: {},
        isSpecificationModalEdit: false,
        reportFlags: {
          showTemperature: false,
          showSymbol: false,
          showDilution: false,
          defaultDilution: null,
        },
        analysis: null,
      };
    },
    computed: {
      ...mapGetters(['tableFilters', 'forms', 'filtersStatus']),
      canUseBulkApply() {
        return acl.userCanUse(['operationsManage']);
      },
      dilutions() {
        return Object.values(DILUTIONS)
          .filter((val) => val !== DILUTIONS.None)
          .map((val) => ({
            value: val,
            label: this.$t(`enums.dilutions.${val}`),
          }));
      },
    },
    watch: {
      'forms.operations': {
        handler() {
          this.getSpecifications();
        },
        deep: true,
      },
      'forms.operations.analysisId': {
        handler(analysisId) {
          this.getAnalysis(analysisId);
          const { showTemperature, showSymbol, showDilution, defaultDilution } =
            this.forms.operations;
          this.reportFlags = analysisId
            ? {
                showTemperature: showTemperature ?? false,
                showSymbol: showSymbol ?? false,
                showDilution: showDilution ?? false,
                defaultDilution: defaultDilution ?? null,
              }
            : {
                showTemperature: false,
                showSymbol: false,
                showDilution: false,
                defaultDilution: null,
              };
        },
      },
    },
    created() {
      const query = { ...this.$route.query };
      this.parseQueryStringStatus(query);
      this.getSpecifications(query);
      this.getAnalysis(this.forms.operations.analysisId);
      if (this.forms.operations.analysisId) {
        const { showTemperature, showSymbol, showDilution, defaultDilution } =
          this.forms.operations;
        this.reportFlags = {
          showTemperature: showTemperature ?? false,
          showSymbol: showSymbol ?? false,
          showDilution: showDilution ?? false,
          defaultDilution: defaultDilution ?? null,
        };
      }
    },
    beforeRouteUpdate(to, from, next) {
      this.parseQueryStringStatus(to.query);
      this.getOperations(to.query);
      this.getSpecifications(to.query);
      next();
    },
    methods: {
      ...mapActions([
        'updateTableRows',
        'updateTableCount',
        'resetTable',
        'updateForm',
        'parseQueryStringStatus',
      ]),
      async getAnalysis(analysisId) {
        this.analysis = null;
        if (!analysisId) return;
        try {
          const { data } = await analysesApi.analysesShow(analysisId);
          if (analysisId === this.forms.operations.analysisId) {
            this.analysis = data;
          }
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response?.data?.error?.message ?? error.message,
          });
        }
      },
      async updateReportFlag(flag, value) {
        const { analysisId } = this.forms.operations;
        try {
          await analysesApi.updateReportFlagsPatch(analysisId, {
            [flag]: value,
          });
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('operations.update.success'),
          });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      updateDefaultDilution(value) {
        this.reportFlags.defaultDilution = value || null;
        this.updateReportFlag(
          'defaultDilution',
          this.reportFlags.defaultDilution
        );
      },
      async getOperations() {
        const analysisId = this.forms.operations.analysisId || null;
        if (analysisId) {
          try {
            const operationsIndex = await analysesApi.operationsIndex(
              analysisId,
              this.tableFilters('operations')
            );
            const operationsCount = await analysesApi.operationsCount(
              analysisId
            );
            this.updateTableRows({
              tableKey: 'operations',
              rows: operationsIndex.data,
            });
            this.updateTableCount({
              tableKey: 'operations',
              count: operationsCount.data.count,
            });
          } catch (error) {
            this.$notify({
              showClose: true,
              type: 'error',
              message: error.response.data.error.message,
            });
          }
        } else {
          this.resetTable('operations');
        }
      },
      async getSpecifications() {
        const analysisId = this.forms.operations.analysisId || null;
        if (analysisId) {
          try {
            const specificationsIndex = await analysesApi.specificationsIndex(
              analysisId,
              this.filtersStatus.specifications
            );
            const groupedSpecifications = specificationsIndex.data.reduce(
              (acc, item) => {
                const key = item.customerId
                  ? `${item.customerId} / ${item.customerProductCode}`
                  : 'Standard';
                acc[key] = [...(acc[key] || []), item];
                return acc;
              },
              {}
            );
            const tableData = Object.keys(groupedSpecifications).map(
              (groupingId) => {
                const id =
                  groupingId === 'Standard'
                    ? -1
                    : -1 * parseInt(groupingId.replace(' / ', ''), 10);
                return {
                  groupingId,
                  id,
                  children: groupedSpecifications[groupingId],
                };
              }
            );
            this.updateTableRows({
              tableKey: 'specifications',
              rows: tableData,
            });
          } catch (error) {
            this.$notify({
              showClose: true,
              type: 'error',
              message: error.response.data.error.message,
            });
          }
        } else {
          this.resetTable('specifications');
        }
      },
      async editOperation(row) {
        const analysisId = this.forms.operations.analysisId || null;
        try {
          const operation = await analysesApi.operationsShow(
            analysisId,
            row.id
          );
          const specification = await analysesApi.specificationsShow(
            analysisId,
            row.id
          );
          this.initialOperationData = {
            operation: {
              ...operation.data,
              measure:
                row.measureId === operation.data.measureId ? row.measure : null,
            },
            specification: specification.data,
          };
          this.isModalEdit = true;
          this.openCreateEditOperation = true;
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      async addCustomSpecification(row) {
        const analysisId = this.forms.operations.analysisId || null;
        const specifications = await analysesApi.specificationsShow(
          analysisId,
          row.id
        );
        const hasStandardSpec = specifications.data.some(
          (spec) => !spec.customerId && !spec.customerProductCode
        );
        this.initialSpecificationData = {
          analysisId: row.analysisId,
          operationRegistryId: row.id,
          measure: row.measure,
          hasStandardSpecification: hasStandardSpec,
        };
        this.isSpecificationModalEdit = false;
        this.openCreateEditSpecification = true;
      },
      async editSpecification(row) {
        this.initialSpecificationData = {
          ...row,
        };
        this.isSpecificationModalEdit = true;
        this.openCreateEditSpecification = true;
      },
      async deleteOperation(row) {
        try {
          await this.$confirm(
            this.$t('operations.delete.confirm'),
            this.$t('global.warning'),
            {
              confirmButtonText: this.$t('global.ok'),
              cancelButtonText: this.$t('global.cancel'),
              type: 'warning',
            }
          ).then(async () => {
            await analysesApi.operationsDelete(
              this.forms.operations.analysisId,
              row.id
            );
            this.$notify({
              showClose: true,
              type: 'success',
              message: this.$t('operations.delete.success'),
            });
            await this.getOperations();
          });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      async patchOperation(row, data) {
        try {
          await analysesApi.operationsPatch(
            this.forms.operations.analysisId,
            row.id,
            data
          );
          this.getOperations();
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('operations.update.success'),
          });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      copySelectionTo(selection) {
        this.copyToSelection = selection;
        this.openCopyToOperations = true;
      },
      handleDialogClose(status) {
        this.openCreateEditOperation = false;
        if (status.success) this.getOperations();
        if (this.isModalEdit) {
          this.isModalEdit = false;
        }
      },
      handleSpecificationDialogClose(status) {
        this.openCreateEditSpecification = false;
        if (status.success) this.getSpecifications();
        if (this.isSpecificationModalEdit) {
          this.isSpecificationModalEdit = false;
        }
      },
      handleAnalysisWithoutOperationDialogOpen() {
        this.openAnalysisWithoutOperations = true;
      },
      handleAnalysisWithoutOperationDialogClose() {
        this.openAnalysisWithoutOperations = false;
      },
      handleCopyToClose({ success, formData }) {
        this.openCopyToOperations = false;
        if (success) {
          this.updateForm({ formKey: 'operations', formData });
          this.getOperations();
        }
      },
      deleteSpecification(row) {
        try {
          this.$confirm(
            this.$t('specifications.delete.confirm'),
            this.$t('global.warning'),
            {
              confirmButtonText: this.$t('global.ok'),
              cancelButtonText: this.$t('global.cancel'),
              type: 'warning',
            }
          )
            .then(async () => {
              await analysesApi.specificationsDelete(
                row.analysisId || '0',
                row.operationRegistryId || '0',
                row.id
              );
              this.$notify({
                showClose: true,
                type: 'success',
                message: this.$t('specifications.delete.success'),
              });
              await this.getSpecifications();
            })
            .catch(() => {});
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
    },
  };
</script>

<style lang="scss">
  .operations-subtitle {
    color: $--color-white;
    padding-bottom: 1rem;
  }

  .operations-subtitle-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
</style>
